<script setup lang="ts">
import type { PropertyOnCard } from '@@/shared/property/types';
import type { PropertyOnList } from '@@/shared/property/api-types';
import { NuxtLinkLocale } from '#components';
import type { IadAppRoute } from '~/application';

const props = defineProps<{
  property: PropertyOnList | PropertyOnCard
  propertyUrl: IadAppRoute<`property-propertySlug-rpropertyListingRef`>
}>();

const { t } = useI18n();
const { title: propertyTitle } = usePropertyTitle(toRef(() => props.property));

const runtimeConfig = useRuntimeConfig();
// max width on responsive design (single image on small devices)
// https://sharp.pixelplumbing.com/api-input
const IMG_WIDTH = 300;
const photos = computed(() => {
  return props.property.photos
    .slice(0, 5)
    .map(src => getImageResize({
      src,
      width: IMG_WIDTH,
      resizeHostname: runtimeConfig.public.img.resizeHostname,
    }));
});
// We don't want duplicated links on SSR (prevents SEO to have to fetch similar links)
const isMounted = useMounted();
const photoTag = computed(() => isMounted.value ? NuxtLinkLocale : 'span');

// Image loading: we want to force load as soon as the carrousel is interacted with
// This will prevent flicker between slides
const isInteracting = ref(false);
function onInteraction() {
  isInteracting.value = true;
}

// NOTE: About markup
// • “isMounted” check are done to avoid loading all images at page load
//   without this “loading="lazy"” won't catch-up and all images will be loaded at once
</script>

<template>
  <IadCarrousel
    class="relative z-10 rounded-t-xl"
    tag="div"
    @enter.once="onInteraction"
  >
    <template v-if="photos.length">
      <picture
        v-for="(photo, index) in photos"
        :key="photo.src"
        :data-index="index"
      >
        <component
          :is="photoTag"
          :to="propertyUrl"
        >
          <img
            v-if="isMounted || index === 0"
            :alt="propertyTitle"
            v-bind="photo"
            :loading="(isInteracting && index > 0)? undefined: 'lazy'"
            :width="IMG_WIDTH"
            class="size-full aspect-[4/3] object-cover"
          >
        </component>
      </picture>

      <picture class="relative">
        <component
          :is="photoTag"
          :to="propertyUrl"
        >
          <img
            v-if="isMounted"
            :alt="propertyTitle"
            v-bind="photos[0]"
            :width="IMG_WIDTH"
            class="size-full aspect-[4/3] object-cover brightness-50"
          >
          <IadBtn
            :text="t(`core.common.see_listing`)"
            class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
          />
        </component>
      </picture>
    </template>
    <component
      :is="photoTag"
      v-else
      :to="propertyUrl"
      class="bg-neutral-80 size-full aspect-[4/3] flex flex-col gap-m items-center justify-center text-secondary"
    >
      <Icon
        name="iad-icon:logo-iad"
        class="size-24"
      />
      <IadTitle
        :text="t(`property.common.no_photo`)"
        tag="p"
        variant="x-small"
      />
    </component>
  </IadCarrousel>
</template>
